import { Orientation } from "@/utils/exif"

export async function loadImage(src: string, signal?: AbortSignal): Promise<HTMLImageElement> {
	const image = new Image()
	image.src = src

	await image.decode()

	signal?.throwIfAborted()

	return image
}

export async function loadImageBitmap(
	src: string,
	signal?: AbortSignal,
	options?: ImageBitmapOptions,
): Promise<ImageBitmap> {
	const blob = await fetch(src, { signal }).then((res) => res.blob())
	return createImageBitmap(blob, options)
}

export async function createOrientedBitmap(blob: Blob, orientation: Orientation | null) {
	const bitmap = await createImageBitmap(blob)

	if (orientation !== 1) {
		const orientedBitmap = await removeBitmapOrientation(bitmap)
		bitmap.close()
		return orientedBitmap
	}

	return bitmap
}

let offscreenBitmapCanvas: OffscreenCanvas | null = null
let offscreenBitmapContext: OffscreenCanvasRenderingContext2D | null = null

export async function removeBitmapOrientation(imageBitmap: ImageBitmap): Promise<ImageBitmap> {
	// This must be task-local: culling can orient multiple previews concurrently.
	const bitmapCanvas = document.createElement("canvas")
	const bitmapContext = bitmapCanvas.getContext("2d")

	if (!bitmapContext) {
		throw new Error("[removeBitmapOrientation] Could not create canvas context")
	}

	bitmapCanvas.width = imageBitmap.width
	bitmapCanvas.height = imageBitmap.height
	bitmapContext.drawImage(imageBitmap, 0, 0, bitmapCanvas.width, bitmapCanvas.height)

	return createImageBitmap(bitmapCanvas)
}

export async function removeBitmapOrientationOffscreen(
	imageBitmap: ImageBitmap,
): Promise<ImageBitmap> {
	if (!offscreenBitmapCanvas || !offscreenBitmapContext) {
		offscreenBitmapCanvas = new OffscreenCanvas(1, 1)
		offscreenBitmapContext = offscreenBitmapCanvas.getContext("2d")
	}

	offscreenBitmapCanvas.width = imageBitmap.width
	offscreenBitmapCanvas.height = imageBitmap.height

	if (!offscreenBitmapContext) {
		throw new Error("[removeBitmapOrientation] Could not create canvas context")
	}

	offscreenBitmapContext.drawImage(
		imageBitmap,
		0,
		0,
		offscreenBitmapCanvas.width,
		offscreenBitmapCanvas.height,
	)

	return createImageBitmap(offscreenBitmapCanvas)
}
